import React, { useEffect, useState } from 'react';
import { Coins, TrendingDown, AlertTriangle } from 'lucide-react';
import { usageService } from '../services/usageService';
import { useChatStore } from '../store/chatStore';
import type { CreditBalance } from '../types';

interface CreditIndicatorProps {
  className?: string;
  compact?: boolean;
}

export const CreditIndicator: React.FC<CreditIndicatorProps> = ({ className = '', compact = false }) => {
  const { selectedWorkspace } = useChatStore();
  const [credits, setCredits] = useState<CreditBalance | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCredits = async () => {
      if (!selectedWorkspace?.id) return;

      setLoading(true);
      setError(null);

      try {
        const response = await usageService.getCreditBalance(selectedWorkspace.id);
        if (response.success && response.data) {
          setCredits(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch credits:', err);
        setError('Unable to load credits');
      } finally {
        setLoading(false);
      }
    };

    fetchCredits();

    // Refresh credits every 5 minutes
    const interval = setInterval(fetchCredits, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [selectedWorkspace?.id]);

  if (loading && !credits) {
    return (
      <div className={`flex items-center gap-2 text-gray-400 ${className}`}>
        <Coins className="h-4 w-4 animate-pulse" />
        {!compact && <span className="text-sm">Loading...</span>}
      </div>
    );
  }

  if (error || !credits) {
    return null; // Don't show anything if we can't load credits
  }

  const { creditsUsed, creditsAllocated, creditsRemaining, percentUsed } = credits;

  // Determine status and styling
  const isLow = percentUsed >= 80;
  const isCritical = percentUsed >= 95;
  const isDepleted = creditsRemaining <= 0;

  const getStatusColor = () => {
    if (isDepleted) return 'text-red-600';
    if (isCritical) return 'text-red-500';
    if (isLow) return 'text-orange-500';
    return 'text-green-600';
  };

  const getBgColor = () => {
    if (isDepleted) return 'bg-red-50';
    if (isCritical) return 'bg-red-50';
    if (isLow) return 'bg-orange-50';
    return 'bg-green-50';
  };

  const formatCredits = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const Icon = isDepleted || isCritical ? AlertTriangle : isLow ? TrendingDown : Coins;

  if (compact) {
    return (
      <div
        className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${getBgColor()} ${getStatusColor()} ${className}`}
        title={`${formatCredits(creditsRemaining)} / ${formatCredits(creditsAllocated)} credits remaining`}
        aria-label={`${formatCredits(creditsRemaining)} of ${formatCredits(creditsAllocated)} credits remaining`}
        role="status"
      >
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        <span className="text-xs font-medium">{formatCredits(creditsRemaining)}</span>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${getBgColor()} ${className}`}
      title={`Credits: ${formatCredits(creditsUsed)} used of ${formatCredits(creditsAllocated)}`}
    >
      <Icon className={`h-4 w-4 ${getStatusColor()}`} aria-hidden="true" />
      <div className="flex flex-col">
        <span className={`text-xs font-medium ${getStatusColor()}`}>
          {formatCredits(creditsRemaining)} credits left
        </span>
        <div className="flex items-center gap-1">
          <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                isDepleted ? 'bg-red-500' : isCritical ? 'bg-red-500' : isLow ? 'bg-orange-500' : 'bg-green-500'
              }`}
              style={{ width: `${Math.min(100, percentUsed)}%` }}
            />
          </div>
          <span className="text-[10px] text-gray-500">{percentUsed}%</span>
        </div>
      </div>
    </div>
  );
};

export default CreditIndicator;
